import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// material-ui
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Chip,
    Breadcrumbs,
    Link as MuiLink,
    Alert,
    LinearProgress,
    Tooltip,
    InputAdornment,
    Slider,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Stack
} from '@mui/material';

// icons
import {
    FolderOpen as FolderOpenIcon,
    InsertDriveFile as FileIcon,
    Folder as FolderIcon,
    CloudUpload as CloudUploadIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Download as DownloadIcon,
    Link as LinkIcon,
    Delete as DeleteIcon,
    ContentCopy as CopyIcon,
    ArrowBack as ArrowBackIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { openSnackbar } from 'store/slices/snackbar';
import { apiClient } from 'config/api';

// ==============================|| PROJECT FILES PAGE ||============================== //
// Route: /workspace/:projectName/files
// Browse workspace files for a project (JWT auth via apiClient).

const formatBytes = (size) => {
    if (size == null) return '—';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let s = Number(size);
    let i = 0;
    while (s >= 1024 && i < units.length - 1) {
        s /= 1024;
        i += 1;
    }
    return `${s.toFixed(s >= 10 || i === 0 ? 0 : 2)} ${units[i]}`;
};

const formatDate = (ts) => {
    if (!ts) return '—';
    try {
        const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
        return d.toLocaleString();
    } catch {
        return String(ts);
    }
};

const joinPath = (...parts) =>
    parts
        .filter(Boolean)
        .map((p) => String(p).replace(/^\/+|\/+$/g, ''))
        .filter(Boolean)
        .join('/');

const ProjectFilesPage = () => {
    const { projectName } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [projectId, setProjectId] = useState(null);
    const [projectSubname, setProjectSubname] = useState('');
    const [loadingProject, setLoadingProject] = useState(true);

    const [currentPath, setCurrentPath] = useState('');
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({}); // filename -> percent
    const [uploading, setUploading] = useState(false);
    const dropRef = useRef(null);

    const [presignOpen, setPresignOpen] = useState(false);
    const [presignTarget, setPresignTarget] = useState(null);
    const [presignOp, setPresignOp] = useState('get');
    const [presignExpires, setPresignExpires] = useState(900);
    const [presignResult, setPresignResult] = useState(null);
    const [presignLoading, setPresignLoading] = useState(false);
    const [presignCountdown, setPresignCountdown] = useState(0);
    const countdownRef = useRef(null);

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);
    const [previewContent, setPreviewContent] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);

    // ===== Resolve projectId from project_name =====
    useEffect(() => {
        let alive = true;
        (async () => {
            setLoadingProject(true);
            try {
                const res = await apiClient.get('/projects/list');
                const list = Array.isArray(res.data) ? res.data : res.data?.projects || [];
                const found = list.find(
                    (p) =>
                        (p.project_name || p.projectName || '').toLowerCase() ===
                        String(projectName || '').toLowerCase()
                );
                if (alive && found) {
                    setProjectId(found.id || found.project_id);
                    setProjectSubname(found.project_subname || found.projectSubname || '');
                } else if (alive) {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: `Không tìm thấy project "${projectName}"`,
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    );
                }
            } catch (err) {
                if (alive)
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: err?.response?.data?.detail || 'Lỗi load project',
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    );
            } finally {
                if (alive) setLoadingProject(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [projectName, dispatch]);

    // ===== Load entries for currentPath =====
    const loadEntries = async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const res = await apiClient.get(`/projects/${projectId}/files/list`, {
                params: { path: currentPath }
            });
            const rows = res.data?.entries || res.data?.files || res.data || [];
            setEntries(Array.isArray(rows) ? rows : []);
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.response?.data?.detail || 'Lỗi tải danh sách file',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
            setEntries([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (projectId) loadEntries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, currentPath]);

    // ===== Countdown for presign URL TTL =====
    useEffect(() => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
        if (presignResult && presignCountdown > 0) {
            countdownRef.current = setInterval(() => {
                setPresignCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownRef.current);
                        countdownRef.current = null;
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
            }
        };
    }, [presignResult]); // eslint-disable-line react-hooks/exhaustive-deps

    // ===== Handlers =====
    const handleNavigateDir = (name) => {
        setCurrentPath(joinPath(currentPath, name));
    };

    const handleBreadcrumb = (idx) => {
        if (idx < 0) {
            setCurrentPath('');
            return;
        }
        const parts = currentPath.split('/').filter(Boolean);
        setCurrentPath(parts.slice(0, idx + 1).join('/'));
    };

    const handleDownload = async (entry) => {
        try {
            const target = joinPath(currentPath, entry.name);
            const res = await apiClient.get(`/projects/${projectId}/files/download`, {
                params: { path: target },
                responseType: 'blob'
            });
            const blob = new Blob([res.data]);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = entry.name;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.response?.data?.detail || 'Tải file lỗi',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    const openPresign = (entry) => {
        setPresignTarget(entry);
        setPresignOp('get');
        setPresignExpires(900);
        setPresignResult(null);
        setPresignCountdown(0);
        setPresignOpen(true);
    };

    const handleCreatePresign = async () => {
        if (!presignTarget) return;
        setPresignLoading(true);
        try {
            const target = joinPath(currentPath, presignTarget.name);
            const res = await apiClient.post('/storage/presign', {
                project_id: projectId,
                path: target,
                operation: presignOp,
                expires_sec: presignExpires
            });
            setPresignResult(res.data);
            setPresignCountdown(presignExpires);
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.response?.data?.detail || 'Tạo presign URL lỗi',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        } finally {
            setPresignLoading(false);
        }
    };

    const handleDelete = async (entry) => {
        if (!window.confirm(`Xóa "${entry.name}"?`)) return;
        try {
            const target = joinPath(currentPath, entry.name);
            await apiClient.delete(`/projects/${projectId}/files`, {
                params: { path: target }
            });
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Đã xóa',
                    variant: 'alert',
                    alert: { color: 'success' }
                })
            );
            loadEntries();
        } catch (err) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: err?.response?.data?.detail || 'Xóa lỗi (cần scope files:write)',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    const handlePreview = async (entry) => {
        setPreviewItem(entry);
        setPreviewOpen(true);
        const ext = (entry.name.split('.').pop() || '').toLowerCase();
        const isText = ['txt', 'json', 'csv', 'md', 'log', 'yaml', 'yml', 'py', 'js'].includes(ext);
        const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
        const isParquet = ext === 'parquet';
        if (isParquet) {
            setPreviewContent('__PARQUET__');
            return;
        }
        if (!isText && !isImage) {
            setPreviewContent('__BINARY__');
            return;
        }
        setPreviewLoading(true);
        try {
            const target = joinPath(currentPath, entry.name);
            const res = await apiClient.get(`/projects/${projectId}/files/download`, {
                params: { path: target },
                responseType: isImage ? 'blob' : 'text'
            });
            if (isImage) {
                setPreviewContent(URL.createObjectURL(res.data));
            } else {
                setPreviewContent(String(res.data).slice(0, 50000));
            }
        } catch (err) {
            setPreviewContent('__ERROR__');
        } finally {
            setPreviewLoading(false);
        }
    };

    // ===== Upload =====
    const handleFilesSelected = (files) => {
        const arr = Array.from(files || []);
        setUploadFiles((prev) => [...prev, ...arr]);
    };

    const runUpload = async () => {
        if (uploadFiles.length === 0) return;
        setUploading(true);
        const progress = {};
        for (const file of uploadFiles) {
            try {
                const formData = new FormData();
                formData.append('file', file);
                await apiClient.post(`/projects/${projectId}/files/upload`, formData, {
                    params: { path: joinPath(currentPath, file.name) },
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (evt) => {
                        if (evt.total) {
                            progress[file.name] = Math.round((evt.loaded / evt.total) * 100);
                            setUploadProgress({ ...progress });
                        }
                    }
                });
                progress[file.name] = 100;
                setUploadProgress({ ...progress });
            } catch (err) {
                progress[file.name] = -1;
                setUploadProgress({ ...progress });
                dispatch(
                    openSnackbar({
                        open: true,
                        message: `Upload "${file.name}" lỗi: ${err?.response?.data?.detail || err.message}`,
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                );
            }
        }
        setUploading(false);
        setUploadFiles([]);
        setUploadProgress({});
        setUploadOpen(false);
        loadEntries();
        dispatch(
            openSnackbar({
                open: true,
                message: 'Upload hoàn tất',
                variant: 'alert',
                alert: { color: 'success' }
            })
        );
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleFilesSelected(e.dataTransfer.files);
    };

    const filteredEntries = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((e) => (e.name || '').toLowerCase().includes(q));
    }, [entries, search]);

    const pathParts = currentPath.split('/').filter(Boolean);

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Đã copy',
                    variant: 'alert',
                    alert: { color: 'success' }
                })
            );
        } catch {
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Copy thất bại',
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    return (
        <MainCard
            title={
                <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                    <FolderOpenIcon />
                    <Typography variant="h3">Project Files</Typography>
                    {projectSubname && (
                        <Typography variant="body1" color="text.secondary">
                            — {projectSubname}
                        </Typography>
                    )}
                    <Chip label={projectName} size="small" variant="outlined" />
                </Box>
            }
            secondary={
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        size="small"
                        onClick={() => navigate('/workspace/storage')}
                    >
                        Trở về
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        size="small"
                        onClick={() => setUploadOpen(true)}
                        disabled={!projectId}
                    >
                        Upload
                    </Button>
                    <IconButton onClick={loadEntries} disabled={!projectId || loading} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Box>
            }
        >
            {loadingProject ? (
                <LinearProgress />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Breadcrumb */}
                    <Card variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Breadcrumbs aria-label="breadcrumb">
                                <MuiLink
                                    component="button"
                                    onClick={() => handleBreadcrumb(-1)}
                                    underline="hover"
                                    color="inherit"
                                >
                                    Home
                                </MuiLink>
                                <MuiLink
                                    component="button"
                                    onClick={() => handleBreadcrumb(-1)}
                                    underline="hover"
                                    color="inherit"
                                >
                                    {projectName}
                                </MuiLink>
                                {pathParts.map((p, idx) => (
                                    <MuiLink
                                        key={idx}
                                        component="button"
                                        onClick={() => handleBreadcrumb(idx)}
                                        underline="hover"
                                        color={idx === pathParts.length - 1 ? 'text.primary' : 'inherit'}
                                    >
                                        {p}
                                    </MuiLink>
                                ))}
                            </Breadcrumbs>
                        </CardContent>
                    </Card>

                    {/* Toolbar */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <TextField
                            size="small"
                            placeholder="Tìm trong thư mục hiện tại..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ minWidth: 280 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {filteredEntries.length} mục
                        </Typography>
                    </Stack>

                    {/* File list */}
                    <Card variant="outlined">
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                            {loading ? (
                                <LinearProgress />
                            ) : filteredEntries.length === 0 ? (
                                <Alert severity="info" sx={{ m: 2 }}>
                                    Thư mục trống
                                </Alert>
                            ) : (
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width={40}></TableCell>
                                                <TableCell>Tên</TableCell>
                                                <TableCell align="right">Size</TableCell>
                                                <TableCell>Modified</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredEntries.map((entry) => {
                                                const isDir =
                                                    entry.is_dir ||
                                                    entry.type === 'dir' ||
                                                    entry.type === 'directory';
                                                return (
                                                    <TableRow key={entry.name} hover>
                                                        <TableCell>
                                                            {isDir ? (
                                                                <FolderIcon color="primary" />
                                                            ) : (
                                                                <FileIcon color="action" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {isDir ? (
                                                                <MuiLink
                                                                    component="button"
                                                                    onClick={() => handleNavigateDir(entry.name)}
                                                                    underline="hover"
                                                                >
                                                                    {entry.name}
                                                                </MuiLink>
                                                            ) : (
                                                                <MuiLink
                                                                    component="button"
                                                                    onClick={() => handlePreview(entry)}
                                                                    underline="hover"
                                                                    color="text.primary"
                                                                >
                                                                    {entry.name}
                                                                </MuiLink>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {isDir ? '—' : formatBytes(entry.size)}
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDate(entry.modified || entry.mtime)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {!isDir && (
                                                                <>
                                                                    <Tooltip title="Preview">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handlePreview(entry)}
                                                                        >
                                                                            <VisibilityIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Download">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDownload(entry)}
                                                                        >
                                                                            <DownloadIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Get Presign URL">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => openPresign(entry)}
                                                                        >
                                                                            <LinkIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Delete (files:write)">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleDelete(entry)}
                                                                        >
                                                                            <DeleteIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Upload dialog */}
            <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload files vào {currentPath || '/'}</DialogTitle>
                <DialogContent>
                    <Box
                        ref={dropRef}
                        onDragOver={onDragOver}
                        onDrop={onDrop}
                        sx={{
                            border: '2px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: 'action.hover',
                            mb: 2
                        }}
                        onClick={() => document.getElementById('files-upload-input').click()}
                    >
                        <input
                            id="files-upload-input"
                            type="file"
                            multiple
                            hidden
                            onChange={(e) => handleFilesSelected(e.target.files)}
                        />
                        <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Kéo thả file vào đây hoặc click để chọn
                        </Typography>
                    </Box>
                    {uploadFiles.length > 0 && (
                        <Stack spacing={1}>
                            {uploadFiles.map((f) => (
                                <Box key={f.name}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="body2">{f.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatBytes(f.size)}
                                        </Typography>
                                    </Box>
                                    {uploadProgress[f.name] != null && (
                                        <LinearProgress
                                            variant="determinate"
                                            value={Math.max(0, uploadProgress[f.name])}
                                            color={uploadProgress[f.name] < 0 ? 'error' : 'primary'}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setUploadOpen(false)} disabled={uploading}>
                        Hủy
                    </Button>
                    <Button
                        variant="contained"
                        onClick={runUpload}
                        disabled={uploading || uploadFiles.length === 0}
                        startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                    >
                        Upload ({uploadFiles.length})
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Presign dialog */}
            <Dialog
                open={presignOpen}
                onClose={() => setPresignOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Presigned URL — {presignTarget?.name}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Operation</InputLabel>
                            <Select
                                value={presignOp}
                                label="Operation"
                                onChange={(e) => setPresignOp(e.target.value)}
                            >
                                <MenuItem value="get">GET (download)</MenuItem>
                                <MenuItem value="put">PUT (upload)</MenuItem>
                            </Select>
                        </FormControl>
                        <Box>
                            <Typography variant="body2" gutterBottom>
                                Expires (giây): {presignExpires}
                            </Typography>
                            <Slider
                                min={60}
                                max={3600}
                                step={60}
                                value={presignExpires}
                                onChange={(e, v) => setPresignExpires(v)}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                        {presignResult && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    URL (còn lại: {presignCountdown}s)
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={presignResult.url || ''}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => copyText(presignResult.url)}
                                                >
                                                    <CopyIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        '& input': { fontFamily: 'monospace', fontSize: '0.75rem' }
                                    }}
                                />
                                <LinearProgress
                                    variant="determinate"
                                    value={
                                        presignExpires > 0
                                            ? (presignCountdown / presignExpires) * 100
                                            : 0
                                    }
                                    sx={{ mt: 1 }}
                                />
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPresignOpen(false)}>Đóng</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreatePresign}
                        disabled={presignLoading}
                        startIcon={presignLoading ? <CircularProgress size={16} /> : <LinkIcon />}
                    >
                        Tạo URL
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Preview dialog */}
            <Dialog
                open={previewOpen}
                onClose={() => {
                    setPreviewOpen(false);
                    setPreviewContent('');
                    setPreviewItem(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{previewItem?.name}</DialogTitle>
                <DialogContent dividers>
                    {previewLoading ? (
                        <LinearProgress />
                    ) : previewContent === '__PARQUET__' ? (
                        <Alert severity="info">
                            Parquet file — click Download để tải về (1.07 MB hoặc kích cỡ thực).
                        </Alert>
                    ) : previewContent === '__BINARY__' ? (
                        <Alert severity="info">Binary file — không preview được. Hãy Download.</Alert>
                    ) : previewContent === '__ERROR__' ? (
                        <Alert severity="error">Không tải được preview</Alert>
                    ) : previewContent.startsWith('blob:') ? (
                        <Box
                            component="img"
                            src={previewContent}
                            alt={previewItem?.name}
                            sx={{ maxWidth: '100%', maxHeight: 600, display: 'block', mx: 'auto' }}
                        />
                    ) : (
                        <Box
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                m: 0,
                                maxHeight: 500,
                                overflow: 'auto'
                            }}
                        >
                            {previewContent}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setPreviewOpen(false);
                            setPreviewContent('');
                        }}
                    >
                        Đóng
                    </Button>
                    {previewItem && (
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(previewItem)}
                        >
                            Download
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </MainCard>
    );
};

export default ProjectFilesPage;
